<script setup lang="ts">
import { fieldStateClass, type FieldState } from '@/utils/field-state'
import { computed, nextTick, ref, useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    mode?: 'decimal' | 'currency'
    currency?: string
    locale?: string
    prefix?: string
    suffix?: string
    minFractionDigits?: number
    maxFractionDigits?: number
    min?: number
    max?: number
    useGrouping?: boolean
    placeholder?: string
    ariaLabel?: string
    disabled?: boolean
    bare?: boolean
    state?: FieldState | null
  }>(),
  {
    mode: 'decimal',
    currency: 'EUR',
    locale: 'de',
    prefix: '',
    suffix: '',
    minFractionDigits: 0,
    maxFractionDigits: 2,
    min: undefined,
    max: undefined,
    useGrouping: true,
    disabled: false,
    bare: false,
    state: null,
  },
)

const appearanceClass = computed(() => {
  if (props.bare) {
    return [
      'w-full bg-transparent px-[var(--field-px)] py-[var(--field-py)] text-base transition-colors disabled:cursor-not-allowed disabled:opacity-50',
      props.state === 'missing'
        ? 'text-warning placeholder:text-warning font-medium'
        : 'text-ink placeholder:text-ink-3',
    ]
  }

  return ['finest-field', fieldStateClass(props.state)]
})

const model = defineModel<number | null>({ default: null })

const attrs = useAttrs()

const inputAttrs = computed(() => {
  const { class: _class, ...rest } = attrs

  return rest
})

const decimalSeparator = computed(
  () => new Intl.NumberFormat(props.locale).formatToParts(1.1).find((part) => part.type === 'decimal')?.value ?? '.',
)

const integerFormatter = computed(
  () =>
    new Intl.NumberFormat(props.locale, {
      maximumFractionDigits: 0,
      useGrouping: props.useGrouping,
    }),
)

const emptyCurrencyValue = computed(() =>
  new Intl.NumberFormat(props.locale, {
    style: 'currency',
    currency: props.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(0)
    .replace('0', '-'),
)

const currencyAffixes = computed(() => {
  if (props.mode !== 'currency') {
    return { prefix: '', suffix: '' }
  }

  const parts = new Intl.NumberFormat(props.locale, {
    style: 'currency',
    currency: props.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(1)

  const firstNumberPart = parts.findIndex((part) => isNumberFormatPart(part))
  let lastNumberPart = firstNumberPart

  for (let index = parts.length - 1; index >= 0; index -= 1) {
    if (isNumberFormatPart(parts[index])) {
      lastNumberPart = index
      break
    }
  }

  if (firstNumberPart === -1) {
    return { prefix: '', suffix: '' }
  }

  return {
    prefix: parts
      .slice(0, firstNumberPart)
      .map((part) => part.value)
      .join(''),
    suffix: parts
      .slice(lastNumberPart + 1)
      .map((part) => part.value)
      .join(''),
  }
})

function formatValue(value: number): string {
  const formatted = new Intl.NumberFormat(props.locale, {
    style: props.mode === 'currency' ? 'currency' : 'decimal',
    currency: props.mode === 'currency' ? props.currency : undefined,
    minimumFractionDigits: props.minFractionDigits,
    maximumFractionDigits: props.maxFractionDigits,
    useGrouping: props.useGrouping,
  }).format(value)

  return `${props.prefix}${formatted}${props.suffix}`
}

const displayValue = computed(() => {
  if (model.value === null) {
    return props.mode === 'currency' && !props.placeholder ? emptyCurrencyValue.value : ''
  }

  return formatValue(model.value)
})

const editedValue = ref<string | null>(null)

const inputValue = computed(() => editedValue.value ?? displayValue.value)

function clamp(value: number): number {
  const lowerBound = props.min === undefined ? value : Math.max(value, props.min)

  return props.max === undefined ? lowerBound : Math.min(lowerBound, props.max)
}

function isDigit(char: string): boolean {
  return char >= '0' && char <= '9'
}

function isNumberFormatPart(part: Intl.NumberFormatPart): boolean {
  return part.type === 'integer' || part.type === 'group' || part.type === 'decimal' || part.type === 'fraction'
}

function isEditableChar(char: string): boolean {
  return isDigit(char) || (props.maxFractionDigits > 0 && char === decimalSeparator.value)
}

function editableCharCount(text: string): number {
  let count = 0
  let hasDecimalSeparator = false

  for (const char of text) {
    if (isDigit(char)) {
      count += 1
      continue
    }

    if (props.maxFractionDigits > 0 && char === decimalSeparator.value && !hasDecimalSeparator) {
      count += 1
      hasDecimalSeparator = true
    }
  }

  return count
}

function editablePosition(text: string, offset: number): number {
  if (offset <= 0) {
    for (let index = 0; index < text.length; index += 1) {
      if (isEditableChar(text[index])) {
        return index
      }
    }

    return text.length
  }

  let count = 0
  let lastEditablePosition = text.length

  for (let index = 0; index < text.length; index += 1) {
    if (!isEditableChar(text[index])) {
      continue
    }

    count += 1
    lastEditablePosition = index + 1

    if (count >= offset) {
      return index + 1
    }
  }

  return lastEditablePosition
}

function parseText(text: string): { display: string; value: number | null } {
  const allowsFractions = props.maxFractionDigits > 0
  let sign = ''
  let integerDigits = ''
  let fractionDigits = ''
  let hasDecimalSeparator = false

  for (const char of text) {
    if (char === '-' && sign === '' && integerDigits === '' && fractionDigits === '') {
      sign = char
      continue
    }

    if (allowsFractions && char === decimalSeparator.value && !hasDecimalSeparator) {
      hasDecimalSeparator = true
      continue
    }

    if (isDigit(char)) {
      if (hasDecimalSeparator) {
        fractionDigits += char
      } else {
        integerDigits += char
      }
    }
  }

  fractionDigits = fractionDigits.slice(0, props.maxFractionDigits)

  const hasDigits = integerDigits !== '' || fractionDigits !== ''

  if (!hasDigits && !hasDecimalSeparator) {
    return { display: sign, value: null }
  }

  const integerValue = Number(integerDigits === '' ? '0' : integerDigits)
  const formattedInteger = integerFormatter.value.format(integerValue)
  const formattedNumber = `${formattedInteger}${hasDecimalSeparator ? decimalSeparator.value : ''}${fractionDigits}`
  const normalizedNumber = Number(`${sign}${integerDigits === '' ? '0' : integerDigits}.${fractionDigits}`)

  return {
    display: `${props.prefix}${sign}${currencyAffixes.value.prefix}${formattedNumber}${currencyAffixes.value.suffix}${props.suffix}`,
    value: Number.isNaN(normalizedNumber) ? null : clamp(normalizedNumber),
  }
}

function restoreCaret(input: HTMLInputElement, offset: number): void {
  void nextTick(() => {
    const position = editablePosition(input.value, offset)

    input.setSelectionRange(position, position)
  })
}

function startEditing(event: FocusEvent): void {
  const input = event.target as HTMLInputElement
  const offset = editableCharCount(input.value.slice(0, input.selectionStart ?? input.value.length))

  editedValue.value = model.value === null ? '' : parseText(displayValue.value).display
  restoreCaret(input, offset)
}

function update(event: Event): void {
  const input = event.target as HTMLInputElement
  const offset = editableCharCount(input.value.slice(0, input.selectionStart ?? input.value.length))
  const parsed = parseText(input.value)

  editedValue.value = parsed.display
  model.value = parsed.value
  restoreCaret(input, offset)
}

function stopEditing(): void {
  editedValue.value = null
}
</script>

<template>
  <div class="relative" :class="attrs.class">
    <input
      v-bind="inputAttrs"
      :value="inputValue"
      type="text"
      inputmode="decimal"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      :disabled="disabled"
      class="text-right tabular-nums focus:outline-none"
      :class="appearanceClass"
      @focus="startEditing"
      @input="update"
      @blur="stopEditing" />
  </div>
</template>
